import React, { useState, useMemo, useRef, useEffect  } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  Button,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import styles from "./index.module.css";
import UploadModal from "../../UploadModal";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";
import { GlobalWorkerOptions, getDocument } from "pdfjs-dist";
import {
  buildArtworkSelectionPayload,
  normalizeArtworkFileEntry,
  normalizeCustomerFileEntry,
} from "../artworkFileUtils";
import {
  appendPromoStoreImageSelection,
  canonicalizeImageUrl,
  normalizePromoStoreImageSelection,
  togglePromoStoreImageSelection,
} from "../asiImageUtils";


const IMAGE_CDN = `https://api.asicentral.com/v1/`;

// ---- helpers ----
const IMG_EXT_RX = /\.(png|jpe?g|gif|webp|bmp|svg)(?:$|[?#])/i;


GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

// simple in-memory cache so we don’t re-render thumbs repeatedly
const pdfThumbCache = new Map(); // key: src -> dataURL

const PdfThumb = ({
  src,
  width = 200,
  height = 200,
  skeleton = "loading…",
  onStart,
  onDone,
  onError,
}) => {
  const [dataUrl, setDataUrl] = useState(() => (src ? pdfThumbCache.get(src) || "" : ""));
  const [failed, setFailed] = useState(false);

  // reset state whenever src changes
  useEffect(() => {
    setFailed(false);
    setDataUrl(src ? pdfThumbCache.get(src) || "" : "");
  }, [src]);

  useEffect(() => {
    let cancelled = false;
    if (!src) return;
    if (dataUrl) return; // already cached / rendered

    onStart && onStart();

    (async () => {
      try {
        const loadingTask = getDocument({ url: src, withCredentials: false });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);

        const v0 = page.getViewport({ scale: 1 });
        const scale = Math.min(width / v0.width, height / v0.height);
        const viewport = page.getViewport({ scale: Math.max(scale, 0.2) });

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d", { willReadFrequently: false });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);

        await page.render({ canvasContext: ctx, viewport }).promise;
        if (cancelled) return;
        const url = canvas.toDataURL("image/png");
        pdfThumbCache.set(src, url);
        setDataUrl(url);
        onDone && onDone();
      } catch (e) {
        if (!cancelled) {
          setFailed(true);
          onError && onError(e);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [src, width, height, dataUrl, onStart, onDone, onError]);

  if (failed) return null;
  if (!dataUrl) {
    return (
      <div style={{ width, height, display: "grid", placeItems: "center", fontSize: 11, color: "#888" }}>
        {skeleton}
      </div>
    );
  }
  return (
    <img
      src={dataUrl}
      alt="PDF preview"
      style={{ width, height, objectFit: "contain" }}
      loading="eager"
      decoding="async"
    />
  );
};


const isPdf = (s = "") => /\.pdf(?:$|[?#])/i.test(s);

// image-sized endpoint (thumbs)
const resolveSrc = (v) =>
  v?.startsWith("http") ? v : `${IMAGE_CDN}${v}?size=normal`;

// raw/original file URL (for previewing non-images like PDFs)
const resolveRaw = (v) => (v?.startsWith("http") ? v : `${IMAGE_CDN}${v}`);

const normalizeSelectedImageUrl = (v = "") => {
  const raw = String(v || "").trim();
  if (!raw) return "";

  // Already full ASI media URL
  if (/^https:\/\/api\.asicentral\.com\/v1\/media\/\d+(?:$|[?#])/i.test(raw)) {
    return raw.includes("?") ? raw : `${raw}?size=normal`;
  }

  // Other full URLs, like uploaded/client files
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  const cleaned = raw.replace(/^\/+/, "").replace(/^v1\//i, "");

  // "media/26715995"
  if (/^media\/\d+(?:$|[?#])/i.test(cleaned)) {
    return `${IMAGE_CDN}${cleaned}${cleaned.includes("?") ? "" : "?size=normal"}`;
  }

  // "26715995"
  if (/^\d+$/.test(cleaned)) {
    return `${IMAGE_CDN}media/${cleaned}?size=normal`;
  }

  return raw;
};

const getExt = (nameOrUrl = "") => {
  const m = String(nameOrUrl).match(/\.([a-z0-9]+)(?:\?|#|$)/i);
  return m ? m[1].toUpperCase() : "FILE";
};

const getFileName = (s = "") => {
  try {
    const u = new URL(s, window.location.origin);
    return decodeURIComponent(u.pathname.split("/").pop() || "File");
  } catch {
    const parts = String(s).split(/[\/?#]/);
    return decodeURIComponent(parts.pop() || "File");
  }
};

const ASI_MEDIA_RX = /^https:\/\/api\.asicentral\.com\/v1\/media\/\d+(?:$|[?#])/i;

const looksLikeImage = (nameOrUrl = "") => {
  const value = String(nameOrUrl || "");

  return (
    IMG_EXT_RX.test(value) ||
    ASI_MEDIA_RX.test(value) ||
    /^\/?v1\/media\/\d+(?:$|[?#])/i.test(value) ||
    /^\/?media\/\d+(?:$|[?#])/i.test(value)
  );
};

const extractUploadedFilePath = (response) => {
  const payload = response?.data?.file || response?.data || response || {};
  if (!payload || typeof payload !== 'object') return '';
  return payload.filePath || payload.Location || payload.url || payload.path || '';
};

const formatDate = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString();
};

const FilesPopup = ({
  open,
  handleDone,
  onClose,
  onImageSelect,
  onImagesSelect,
  onSelectFile,
  imageGroups,
  selectedImage,
  selectedImages = [],
  customerId,
  allowUpload = true,
  promoStoreProductGalleryMode = false,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [broken, setBroken] = useState({}); // key -> true when <img> failed
  const [copiedKey, setCopiedKey] = useState("");

  const tabKeys = useMemo(() => Object.keys(imageGroups || {}), [imageGroups]);

  const handleTabChange = (_, newValue) => setActiveTab(newValue);

  const handleClose = () => {
    if (promoStoreProductGalleryMode) {
      const normalized = normalizePromoStoreImageSelection({
        selectedImage,
        imageUrls: selectedImages,
      });
      onImagesSelect?.(normalized.imageUrls);
      onImageSelect(normalized.selectedImage);
      onClose();
      return;
    }
    onImageSelect(selectedImage);
    onClose();
  };

  const handleDialogClose = (_, reason) => {
    if (uploadOpen) return;
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    handleClose();
  };

  const handleImageClick = (entry) => {
    const normalized = normalizeCustomerFileEntry(entry);
    const selection = buildArtworkSelectionPayload(normalized);
    const nextUrl = normalizeSelectedImageUrl(selection.originalUrl);

    if (promoStoreProductGalleryMode) {
      if (!looksLikeImage(nextUrl)) return;
      const nextImages = togglePromoStoreImageSelection(selectedImages, nextUrl);
      const nextPrimary = nextImages[0] || "";
      if (nextPrimary) setSelLoading(true);
      onImagesSelect?.(nextImages);
      onImageSelect(nextPrimary);
      onSelectFile?.(selection);
      return;
    }

    setSelLoading(true);
    onImageSelect(nextUrl);
    onSelectFile?.(selection);
  };

  const handleUploadDone = async (cid, payload) => {
    const response = await handleDone?.(cid, payload);
    const uploadedEntry = normalizeCustomerFileEntry(response?.data?.file || response?.data || response);
    const uploadedSelection = buildArtworkSelectionPayload(uploadedEntry);
    const uploadedPath = uploadedEntry.filePath || extractUploadedFilePath(response);
    if (uploadedPath) {
      const normalizedUploadedPath = normalizeSelectedImageUrl(uploadedPath);
      if (promoStoreProductGalleryMode) {
        if (looksLikeImage(normalizedUploadedPath)) {
          const nextImages = appendPromoStoreImageSelection(selectedImages, normalizedUploadedPath);
          const nextPrimary = nextImages[0] || "";
          if (nextPrimary) setSelLoading(true);
          onImagesSelect?.(nextImages);
          onImageSelect(nextPrimary);
        }
        onSelectFile?.({
          ...uploadedSelection,
          originalUrl: uploadedSelection.originalUrl || normalizedUploadedPath,
          filePath: uploadedPath,
          fileName:
            uploadedSelection.fileName ||
            payload?.file?.name ||
            getFileName(uploadedPath),
        });
        return response;
      }

      setSelLoading(true);
      onImageSelect(uploadedPath);
      onSelectFile?.({
        ...uploadedSelection,
        originalUrl: uploadedSelection.originalUrl || uploadedPath,
        filePath: uploadedPath,
        fileName:
          uploadedSelection.fileName ||
          payload?.file?.name ||
          getFileName(uploadedPath),
      });
    }
    return response;
  };
  

  const [selLoading, setSelLoading] = useState(false);
  const normalizedSelectedImages = useMemo(
    () => (
      promoStoreProductGalleryMode
        ? normalizePromoStoreImageSelection({
            selectedImage,
            imageUrls: selectedImages,
          }).imageUrls
        : []
    ),
    [promoStoreProductGalleryMode, selectedImage, selectedImages]
  );
  const selectedImageOrder = useMemo(() => {
    const map = new Map();
    normalizedSelectedImages.forEach((image, index) => {
      const key = canonicalizeImageUrl(image);
      if (key) map.set(key, index);
    });
    return map;
  }, [normalizedSelectedImages]);

  const primarySelectedImage = promoStoreProductGalleryMode
    ? normalizedSelectedImages[0] || selectedImage || ""
    : selectedImage;

  // whenever the selectedImage prop changes, show loading until the new preview finishes
  useEffect(() => {
    if (!primarySelectedImage) { setSelLoading(false); return; }
    setSelLoading(true);
  }, [primarySelectedImage]);

  const markBroken = (key) => setBroken((p) => ({ ...p, [key]: true }));
  const markCopied = (key) => {
    setCopiedKey(key);
    window.clearTimeout(markCopied._t);
    markCopied._t = window.setTimeout(() => setCopiedKey(""), 1500);
  };
  // Selected preview (top)
  const selHas = !!primarySelectedImage;
  const selSrc = selHas ? resolveSrc(primarySelectedImage) : "";
  const selRaw = selHas ? resolveRaw(primarySelectedImage) : "";
  const selIsImg = selHas && looksLikeImage(primarySelectedImage);
  const selKey = selHas ? `sel::${primarySelectedImage}` : "";
  const selShowImg = selHas && selIsImg && !broken[selKey];
  const selExt = getExt(primarySelectedImage);
  const selName = getFileName(primarySelectedImage);

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="lg">
        <DialogTitle className={styles.title}>Select Image</DialogTitle>
        <DialogContent className={styles.dialogContent}>
  {selHas && (
    <Box mb={2} display="flex" gap={2} alignItems="center">
      <Tooltip title={`${selName}`}>
        <Box
          className={styles.selectedThumbnail} 
          sx={{
            border: "1px dashed #bdbdbd",
            borderRadius: "8px",
            width: 200,
            height: 200,
            display: "grid",
            placeItems: "center",
            bgcolor: "#fafafa",
            overflow: "hidden",
            position: "relative",
          }}
        >
{selShowImg ? (
  <img
    src={selSrc}
    alt="selected-preview"
    className={styles.selectedImage}
    style={{ width: "100%", height: "100%", objectFit: "contain" }}
    onLoad={() => setSelLoading(false)}
    onError={() => { markBroken(selKey); setSelLoading(false); }}
    decoding="async"
    loading="eager"
  />
) : isPdf(primarySelectedImage) ? (
  <PdfThumb
    src={selRaw}
    width={200}
    height={200}
    onStart={() => setSelLoading(true)}
    onDone={() => setSelLoading(false)}
    onError={() => setSelLoading(false)}
  />
) : (
  <Box sx={{ textAlign: "center" }}>
    <InsertDriveFileOutlinedIcon />
    <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
      {selExt}
    </Typography>
  </Box>
)}

{/* Loading badge/overlay */}
{selHas && selLoading && (
  <Box
    sx={{
      position: "absolute",
      bottom: 6,
      left: 6,
      px: 1,
      py: 0.25,
      bgcolor: "rgba(0,0,0,0.6)",
      color: "white",
      borderRadius: "6px",
      fontSize: 12,
    }}
  >
    Loading…
  </Box>
)}

        </Box>
      </Tooltip>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {promoStoreProductGalleryMode ? "Selected product images" : "Current selection"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {selName}
        </Typography>
        {promoStoreProductGalleryMode ? (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Select one or more product images. Image 1 will be the primary image.
            </Typography>
            {normalizedSelectedImages.map((image, index) => (
              <Typography key={`selected-${index}`} variant="body2" color="text.secondary">
                {index + 1}. {getFileName(image)}
              </Typography>
            ))}
          </Box>
        ) : null}
        {!selShowImg && selHas && (
          <Button
            size="small"
            sx={{ mt: 1, px: 1.25 }}
            onClick={() => window.open(selRaw, "_blank", "noopener,noreferrer")}
          >
            Open preview
          </Button>
        )}
      </Box>
    </Box>
  )}

  <Typography variant="subtitle1">
    {promoStoreProductGalleryMode
      ? "Select or remove product gallery images"
      : "Click images below to replace"}
  </Typography>

  <Box className={styles.tabsUploadRow}>
    <Tabs
      value={activeTab}
      onChange={handleTabChange}
      TabIndicatorProps={{ style: { display: "none" } }}
      className={styles.tabsRoot}
    >
      {tabKeys.map((key, idx) => (
        <Tab
          key={idx}
          label={key}
          sx={{
            fontWeight: 700,
            backgroundColor: activeTab === idx ? "#d3d3d3" : "transparent",
            borderBottom:
              activeTab === idx ? "3px solid #af0000" : "3px solid transparent",
            color: activeTab === idx ? "white" : "inherit",
            borderRadius: "4px 4px 0px 0px",
            marginLeft: "8px",
            "& textColorPrimary": {
              color: "white !important",
            },
          }}
          className={`${styles.tabRoot} ${
            activeTab === idx ? styles.tabSelected : ""
          }`}
        />
      ))}
    </Tabs>

    {allowUpload ? (
      <Button
        variant="outlined"
        onClick={() => setUploadOpen(true)}
        className={styles.uploadBtn}
      >
        Upload
      </Button>
    ) : null}
  </Box>

  <Box className={styles.imageGrid}>
    {imageGroups[tabKeys[activeTab]]?.map((entry, idx) => {
      const normalized = normalizeCustomerFileEntry(entry);
      const img = normalized.originalUrl || normalized.filePath;
      const previewImg = normalized.previewImageUrl || "";
      const key = `${tabKeys[activeTab]}::${idx}::${img}`;
      const fallbackImageSrc = looksLikeImage(img) ? img : "";
      const previewFailed = Boolean(broken[key] && previewImg);
      const effectiveImgSrc = previewFailed ? (fallbackImageSrc || img) : (previewImg || img);
      const src = resolveSrc(effectiveImgSrc);
      const raw = resolveRaw(img);
      const isImg = previewFailed ? Boolean(fallbackImageSrc) : (Boolean(previewImg) || looksLikeImage(img));
      const showImg = isImg && (!broken[key] || previewFailed);
      const pdfFile = isPdf(img);
      const ext = getExt(img);
      const name = normalized.fileName || getFileName(img);
      const selectedKey = canonicalizeImageUrl(img);
      const selectedOrder = selectedImageOrder.get(selectedKey);
      const selected = promoStoreProductGalleryMode
        ? selectedOrder != null
        : selectedImage === img;
      const status = normalized.status;
      const folder = normalized.folder;
      const createdAt = formatDate(normalized.createdAt);
      const canSelect = promoStoreProductGalleryMode ? looksLikeImage(img) : true;

      const handleCopy = async (event) => {
        event.stopPropagation();
        if (!raw) return;
        try {
          await navigator.clipboard?.writeText(raw);
          markCopied(key);
        } catch (e) {
          console.warn("copy failed", e);
        }
      };

      const handleOpen = (event) => {
        event.stopPropagation();
        if (raw) window.open(raw, "_blank", "noopener,noreferrer");
      };

      return (
        <Box
          key={key}
          className={`${styles.tile} ${selected ? styles.tileSelected : ""}`}
          onClick={() => {
            if (!canSelect) return;
            handleImageClick(normalized);
          }}
          title={name}
          sx={{
            cursor: canSelect ? "pointer" : "default",
            opacity: canSelect ? 1 : 0.7,
            position: "relative",
          }}
        >
          {selectedOrder != null ? (
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                minWidth: 24,
                height: 24,
                px: 0.75,
                borderRadius: "999px",
                backgroundColor: "#c3171d",
                color: "#fff",
                display: "grid",
                placeItems: "center",
                fontSize: 12,
                fontWeight: 700,
                zIndex: 2,
              }}
            >
              {selectedOrder + 1}
            </Box>
          ) : null}
          {showImg ? (
            <img
              src={src}
              alt={`preview-${idx}`}
              className={styles.thumbnail}
              onError={() => markBroken(key)}
              decoding="async"
              loading="eager"
            />
          ) : pdfFile ? (
            <PdfThumb src={raw} width={150} height={150} skeleton="…" />
          ) : (
            <Box sx={{ textAlign: "center", pointerEvents: "none" }}>
              <InsertDriveFileOutlinedIcon />
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
              >
                {ext}
              </Typography>
            </Box>
          )}

          <Box className={styles.tileOverlay}>
            <Box className={styles.tileMeta}>
              <Typography variant="caption" sx={{ color: "inherit", fontWeight: 600 }}>
                {name}
              </Typography>
              {folder && (
                <Typography variant="caption" sx={{ color: "inherit" }}>
                  {folder}
                </Typography>
              )}
              {status && (
                <Typography variant="caption" sx={{ color: "inherit" }}>
                  Status: {status}
                </Typography>
              )}
              {createdAt && (
                <Typography variant="caption" sx={{ color: "inherit" }}>
                  Uploaded: {createdAt}
                </Typography>
              )}
              {promoStoreProductGalleryMode && !canSelect && (
                <Typography variant="caption" sx={{ color: "inherit" }}>
                  View only in PromoStore gallery mode
                </Typography>
              )}
            </Box>

            <Box className={styles.tileActions}>
              <Tooltip title={copiedKey === key ? "Copied" : "Copy link"} arrow>
                <IconButton
                  size="small"
                  onClick={handleCopy}
                  sx={{ bgcolor: "rgba(255,255,255,0.9)" }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open" arrow>
                <IconButton
                  size="small"
                  onClick={handleOpen}
                  sx={{ bgcolor: "rgba(255,255,255,0.9)" }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download" arrow>
                <IconButton
                  size="small"
                  component="a"
                  href={raw}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  sx={{ bgcolor: "rgba(255,255,255,0.9)" }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {!showImg && (
                <Tooltip title="Preview" arrow>
                  <IconButton
                    size="small"
                    onClick={handleOpen}
                    sx={{ bgcolor: "rgba(255,255,255,0.9)" }}
                  >
                    <VisibilityOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      );
    })}
  </Box>
</DialogContent>


        <DialogActions>
          <Button onClick={handleClose} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Modal */}
      {allowUpload ? (
        <UploadModal
          handleDone={handleUploadDone}
          open={uploadOpen}
          customerId={customerId}
          onClose={() => setUploadOpen(false)}
        />
      ) : null}
    </>
  );
};

export default FilesPopup;
